import { useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = 'pv_session_id';

/** Generates / retrieves a per-tab session id (sessionStorage). */
function getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      // Crypto-random 16 bytes hex
      const arr = new Uint8Array(16);
      (window.crypto || window.msCrypto).getRandomValues(arr);
      sid = Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('');
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return 'anon-' + Date.now();
  }
}

/** Sends a single page-view beacon. Silent on errors. */
async function sendBeacon(path, locale) {
  try {
    await axios.post(
      `${API}/analytics/track`,
      {
        path: path || window.location.pathname,
        referrer: document.referrer || '',
        session_id: getSessionId(),
        locale: locale || (navigator.language || 'it').slice(0, 2),
      },
      { timeout: 4000 }
    );
  } catch {
    /* silent */
  }
}

/**
 * Lightweight, anonymous page-view tracker.
 * - Mount once near the root.
 * - Sends one beacon on mount, and one on each hash / popstate change.
 * - Refreshes the "online now" heartbeat every 60s while the tab is open.
 */
export default function AnalyticsBeacon({ locale = 'it' }) {
  useEffect(() => {
    // Skip admin pages
    if (window.location.pathname.startsWith('/admin')) return;

    let alive = true;
    sendBeacon(window.location.pathname + window.location.hash, locale);

    const onChange = () => {
      if (!alive) return;
      sendBeacon(window.location.pathname + window.location.hash, locale);
    };
    window.addEventListener('hashchange', onChange);
    window.addEventListener('popstate', onChange);

    // Heartbeat every 60s to keep "online now" counter accurate
    const hb = setInterval(() => {
      if (!alive || document.hidden) return;
      sendBeacon(window.location.pathname + window.location.hash, locale);
    }, 60 * 1000);

    return () => {
      alive = false;
      window.removeEventListener('hashchange', onChange);
      window.removeEventListener('popstate', onChange);
      clearInterval(hb);
    };
  }, [locale]);

  return null;
}
